import React, { useState, useEffect } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { Box } from "@mui/material";

import SwitchPanel from "./SwitchPanel";
import DevicePanel from "./DevicePanel";
import NetworkPanel from "./NetworkPanel";
import InternetPanel from "./InternetPanel";
import AdvancePanel from "./AdvancePanel";
import WirelessPanel from "./WirelessPanel";
import RadioPanel from "./RadioPanel";
import UnityProxyPanel from "./UnityProxyPanel";
import SurveillancePanel from "./SurveillancePanel";
import CameraLiveViewPanel from "./CameraLiveViewPanel";

const GeneralContent = ({
  fetchFlag,
  auth,
  infoData,
  isInventoryDevice,
  networkData,
  capabilityData,
  portData,
  /* wirelessRadioData, */
  wirelessNetworkData,
  fetchTableData,
  clientRowData,
  routerInternetData,
  apRadioData
}) => {
  const deviceType = infoData.deviceType;
  const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
  // const isNcSwitchDevice =
  //   infoData.deviceType === "switch" && infoData.managedProtocol === "nc";

  const { sendBatch } = useDataSourceManager();

  const [batchLoading, setBatchLoading] = useState(false)

  const [deviceTagsUuid, _setDeviceTagsUuid] = useState(uuidv4());
  const [deviceTags, setDeviceTags] = useState([]);

  const [timeZoneUuid, _setTimeZoneUuid] = useState(uuidv4());
  const [timeZone, setTimeZone] = useState("");

  const [deviceAccountUuid, _setDeviceAccountUuid] = useState(uuidv4());
  const [deviceAccount, setDeviceAccount] = useState({
    userName: "",
    secret: "",
  });
  const [showSecret, setShowSecret] = useState(false);

  const [advanceUuid, _setAdvanceUuid] = useState(uuidv4());
  const [advanceData, setAdvanceData] = useState({});

  const [deviceSettingsUuid, _setDeviceSettingsUuid] = useState(uuidv4());
  const [wirelessRadioData, setWirelessRadioData] = useState([]);

  const formatWirelessRadioData = (radioData) => {
    if (!Array.isArray(radioData)) return [];
    return radioData.map((item) => item?.config).filter(Boolean);
  };

  function fetchDeviceTags() {
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/tags/deviceTags.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      requestUuid: deviceTagsUuid,
      restfulPlaceholders: restfulParams,
    }
  }

  function fetchSettingsGeneral() {
    const baseOrgUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    const baseSiteUrl = standalone
      ? `/orgs/:orgId/sites/:siteId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const requestConfig = {
      org: {
        apiKey: `${baseOrgUrl}/settings/general.read`,
        restfulParams: standalone
          ? { orgId: auth.realm.id }
          : { entId: auth.realm.id, orgId: auth.orgs.id },
      },
      site: {
        apiKey: `${baseSiteUrl}/settings/general.read`,
        restfulParams: standalone
          ? { orgId: auth.realm.id, siteId: auth.sites?.id }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: auth.sites?.id,
          },
      },
    };

    const readConfig = requestConfig[auth.scopeLevel];
    return {
      apiKey: readConfig.apiKey,
      requestUuid: timeZoneUuid,
      restfulPlaceholders: readConfig.restfulParams,
    }
  }

  function fetchDeviceAccount() {
    setShowSecret(false);
    const siteBaseUrl = standalone
      ? `/orgs/:orgId/sites/:siteId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const requestConfig = {
      inventory_org: {
        baseUrl: standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`,
        endpoint: "settings/inventoryDeviceAccount.read",
        restfulParams: standalone
          ? { orgId: auth.realm.id }
          : { entId: auth.realm.id, orgId: auth.orgs.id },
      },
      inventory_site: {
        baseUrl: siteBaseUrl,
        endpoint: "devices/inventoryDeviceAccount.read",
        restfulParams: standalone
          ? { orgId: auth.realm.id, siteId: auth.sites?.id }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: auth.sites?.id,
          },
      },
      default: {
        baseUrl: siteBaseUrl,
        endpoint: "settings/deviceAccount.read",
        restfulParams: standalone
          ? { orgId: auth.realm.id, siteId: infoData.siteId }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: infoData.siteId,
          },
      },
    };

    let apikey = "default";
    if (isInventoryDevice && auth.scopeLevel === "org")
      apikey = "inventory_org";
    else if (isInventoryDevice && auth.scopeLevel === "site")
      apikey = "inventory_site";

    const { baseUrl, endpoint, restfulParams } = requestConfig[apikey];

    return {
      apiKey: `${baseUrl}/${endpoint}`,
      requestUuid: deviceAccountUuid,
      restfulPlaceholders: restfulParams,
    }
  }

  function fetchSettingsWiredConfig() {
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/wiredConfig.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    // sendRequest(apiUrl, advanceUuid, restfulParams);
    return {
      apiKey: apiUrl,
      requestUuid: advanceUuid,
      restfulPlaceholders: restfulParams,
    };
  }

  function fetchDeviceSettings() {
    const baseOrgUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    const baseSiteUrl = standalone
      ? `/orgs/:orgId/sites/:siteId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const deviceIdValue =
      ["proxy", "ipcam", "nvr"].includes(infoData.deviceType)
        ? infoData.id
        : infoData.macAddress;

    const requestConfig = {
      org: {
        apiKey: `${baseOrgUrl}/devices/:deviceId/settings.read`,
        restfulParams: standalone
          ? {
            orgId: auth.realm.id,
            deviceId: deviceIdValue,
          }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            deviceId: deviceIdValue,
          },
      },
      site: {
        apiKey: `${baseSiteUrl}/devices/:deviceId/settings.read`,
        restfulParams: standalone
          ? {
            orgId: auth.realm.id,
            siteId: auth.sites?.id,
            deviceId: deviceIdValue,
          }
          : {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: auth.sites?.id,
            deviceId: deviceIdValue,
          },
      },
    };

    const readConfig = requestConfig[auth.scopeLevel];

    return {
      apiKey: readConfig.apiKey,
      requestUuid: deviceSettingsUuid,
      restfulPlaceholders: readConfig.restfulParams,
    };
  }

  const fetchBatchGeneralData = async () => {
    setBatchLoading(true)
    // deviceTags
    const deviceTagsParams = fetchDeviceTags()
    // settingsGeneral
    const settingsGeneralParams = fetchSettingsGeneral()
    // deviceAccount
    const deviceAccountParams = fetchDeviceAccount()
    // settingsWiredConfig
    const settingsWiredConfigParams = fetchSettingsWiredConfig()

    const deviceSettingsParams = fetchDeviceSettings();

    const { getSendBatchData } = await sendBatch([
      deviceTagsParams,
      deviceSettingsParams,
      ...(capabilityData.tunnelAccess === false
        ? [settingsGeneralParams, deviceAccountParams]
        : [deviceAccountParams]),
      ...(deviceType === "switch" && !isInventoryDevice
        ? [settingsWiredConfigParams]
        : []),
    ]);

    const deviceTagsData = getSendBatchData(deviceTagsParams)?.data;
    const deviceAccountData = getSendBatchData(deviceAccountParams)?.data;
    const deviceSettingsData = getSendBatchData(deviceSettingsParams)?.data;

    setDeviceTags(deviceTagsData);
    setDeviceAccount({
      // 舊規則 :
      // NC Switch 不支援修改 username，因此前端針對 NC Switch 永遠顯示 "admin"
      // 新規則 ( #32814 ) :
      // NC Switch 不用擋顯示 username 了

      // userName: isNcSwitchDevice ? "admin" : data.username,
      userName: deviceAccountData.username,
      secret: deviceAccountData.password,
    });


    setWirelessRadioData(
      formatWirelessRadioData(deviceSettingsData?.["wireless.radio"])
    );

    // settingsGeneral timezone
    if (capabilityData.tunnelAccess === false) {
      const timeZoneData = getSendBatchData(settingsGeneralParams)?.data.timeConfig.timezone;
      setTimeZone(timeZoneData)
    }

    // settingsWiredConfig
    if (deviceType === "switch" && !isInventoryDevice) {
      const settingsWiredConfigData = getSendBatchData(settingsWiredConfigParams)?.data;
      const advance = settingsWiredConfigData && settingsWiredConfigData.length > 0
        ? settingsWiredConfigData[0]
        : {};

      const parseState = (val, mockDefault = 'Disabled') => {
        if (val === true) return 'Enabled';
        if (val === false) return 'Disabled';
        if (typeof val === 'string' && val.length > 0) return val;
        return mockDefault;
      };

      setAdvanceData({
        IGMPSnooping: parseState(advance?.IGMPSnoopingState ?? advance?.IGMPSnooping, 'Enabled'),
        DDP: parseState(advance?.DDPState ?? advance?.DDP, 'Disabled'),
        RSTP: parseState(advance?.RSTPState ?? advance?.RSTP, 'Disabled'),
        LBD: parseState(advance?.LBDState ?? advance?.LBD, 'Disabled'),
      })
    }

    setBatchLoading(false)
  }

  useEffect(() => {
    fetchBatchGeneralData()
  }, [auth, infoData.siteId, infoData.macAddress, infoData.id, infoData.deviceType])

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      {deviceType === "proxy" ? (
        <>
          <Box width="100%" padding="16px">
            <UnityProxyPanel
              auth={auth}
              infoData={infoData}
              capabilityData={capabilityData}
              networkData={networkData}
            />
          </Box>
          <Box width="100%" padding="16px">
            <NetworkPanel networkData={networkData} infoData={infoData} />
          </Box>
        </>
      ) : (
        <>
          {deviceType === "switch" && (
            <Box width="100%" padding="16px">
              <SwitchPanel
                infoData={infoData}
                capabilityData={capabilityData}
                portData={portData}
              />
            </Box>
          )}

          {deviceType === "ipcam" && (
            <Box width="100%" padding="16px">
              <CameraLiveViewPanel auth={auth} infoData={infoData} />
            </Box>
          )}

          {deviceType !== "nvr" && deviceType !== "ipcam" && (
            <Box width="100%" padding="16px">
              <DevicePanel
                fetchFlag={fetchFlag}
                auth={auth}
                standalone={standalone}
                infoData={infoData}
                capabilityData={capabilityData}
                batchLoading={batchLoading}
                deviceTags={deviceTags}
                setDeviceTags={setDeviceTags}
                timeZone={timeZone}
                deviceAccount={deviceAccount}
                showSecret={showSecret}
                setShowSecret={setShowSecret}
                fetchTableData={fetchTableData}
              />
            </Box>
          )}

          {deviceType !== "router" && deviceType !== "nvr" && deviceType !== "ipcam" && (
            <Box width="100%" padding="16px">
              <NetworkPanel networkData={networkData} infoData={infoData} />
            </Box>
          )}

          {deviceType === "router" && (
            <Box width="100%" padding="16px">
              <InternetPanel routerInternetData={routerInternetData} />
            </Box>
          )}

          {deviceType === "switch" && !isInventoryDevice && (
            <Box width="100%" padding="16px">
              <AdvancePanel
                capabilityData={capabilityData}
                advanceLoading={batchLoading}
                advanceData={advanceData}
              />
            </Box>
          )}

          {(deviceType === "nvr" || deviceType === "ipcam") && (
            <Box width="100%" padding="16px">
              <SurveillancePanel
                infoData={infoData}
                networkData={networkData}
                timeZone={timeZone}
                batchLoading={batchLoading}
              />
            </Box>
          )}

          {deviceType === "ap" && (
            <>
               <Box width="100%" padding="16px">
                 <WirelessPanel
                   auth={auth}
                   infoData={infoData}
                   wirelessNetworkData={wirelessNetworkData}
                   capabilityData={capabilityData}
                 />
               </Box>
               <Box width="100%" padding="16px">
                 <RadioPanel wirelessRadioData={wirelessRadioData} clientRowData={clientRowData} apRadioData={apRadioData}/>
               </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default GeneralContent;
